import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileApi, formatApiError, resolveImageUrl } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { User, Upload, Trash2, Check, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function ProfileEditPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const firstVisit = location.state?.newUser === true;

  const [form, setForm] = useState({
    name: '',
    display_name_pref: 'full',
    hometown: '',
    local_track: '',
    biggest_win: '',
    bio: '',
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === false) {
      navigate('/login', { state: { from: { pathname: '/profile/edit' } } });
      return;
    }
    if (user && user.profile) {
      setForm({
        name: user.profile.full_name || user.name || '',
        display_name_pref: user.profile.display_name_pref || 'full',
        hometown: user.profile.hometown || '',
        local_track: user.profile.local_track || '',
        biggest_win: user.profile.biggest_win || '',
        bio: user.profile.bio || '',
      });
      setAvatarUrl(user.profile.avatar_url);
    }
  }, [user, navigate]);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await profileApi.updateMe(form);
      await checkAuth();
      flash('Profile saved');
      if (firstVisit) navigate(`/profile/${user.id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5 MB)');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const res = await profileApi.uploadAvatar(file);
      setAvatarUrl(res.data.avatar_url);
      await checkAuth();
      flash('Photo updated');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await profileApi.deleteAvatar();
      setAvatarUrl(null);
      await checkAuth();
      flash('Photo removed');
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user) return null;

  const preview = form.display_name_pref === 'first'
    ? (form.name || '').split(' ')[0]
    : form.name;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-2">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="tag text-[#FF3B30] mb-2">My Account</p>
              <h1 className="font-heading font-bold text-3xl text-white">
                {firstVisit ? 'Welcome! Complete Your Profile' : 'Edit Profile'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {firstVisit
                  ? 'Share a bit about your racing — it helps the community connect. You can skip or edit anytime.'
                  : 'Keep your profile fresh. Changes save instantly.'}
              </p>
            </div>
            {user?.id && (
              <Link to={`/profile/${user.id}`}>
                <Button variant="outline" className="border-[#262626] text-white" data-testid="view-my-profile-btn">
                  <Eye className="w-4 h-4 mr-2" /> View Public Profile
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {toast && (
          <div className="bg-green-500/20 border border-green-500/40 text-green-400 px-3 py-2 rounded text-sm mb-4 flex items-center gap-2">
            <Check className="w-4 h-4" /> {toast}
          </div>
        )}
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-400 px-3 py-2 rounded text-sm mb-4">{error}</div>
        )}

        <Card className="bg-[#141414] border-[#262626] p-6 mb-6">
          <h3 className="font-heading font-bold text-white mb-4">Profile Photo</h3>
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-full bg-[#1F1F1F] border border-[#262626] flex items-center justify-center overflow-hidden flex-shrink-0">
              {avatarUrl ? (
                <img src={resolveImageUrl(avatarUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-gray-600" />
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
                data-testid="avatar-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#FF3B30] hover:bg-[#E6352B]"
                data-testid="upload-avatar-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading…' : avatarUrl ? 'Replace Photo' : 'Upload Photo'}
              </Button>
              {avatarUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAvatarDelete}
                  className="border-[#262626] text-red-400 hover:text-red-300"
                  data-testid="delete-avatar-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                </Button>
              )}
            </div>
          </div>
        </Card>

        <form onSubmit={handleSave}>
          <Card className="bg-[#141414] border-[#262626] p-6 mb-6">
            <h3 className="font-heading font-bold text-white mb-4">Basic Info</h3>
            <div className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label>Display Preference</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[
                    { value: 'full', label: 'Show full name', example: form.name || 'John Smith' },
                    { value: 'first', label: 'First name only', example: (form.name || 'John').split(' ')[0] },
                  ].map((opt) => {
                    const active = form.display_name_pref === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, display_name_pref: opt.value })}
                        className={`p-3 border rounded text-left transition-colors ${
                          active ? 'border-[#FF3B30] bg-[#FF3B30]/10' : 'border-[#262626] hover:border-[#FF3B30]/50'
                        }`}
                        data-testid={`pref-${opt.value}`}
                      >
                        <div className="flex items-center gap-2">
                          {active ? <Eye className="w-4 h-4 text-[#FF3B30]" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
                          <span className={`text-sm font-medium ${active ? 'text-[#FF3B30]' : 'text-white'}`}>{opt.label}</span>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">Example: <span className="text-gray-300">{opt.example}</span></p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-gray-500 text-xs mt-2">This is shown next to your posts, comments and profile card.</p>
              </div>
              <div>
                <Label>Hometown</Label>
                <Input
                  value={form.hometown}
                  onChange={(e) => setForm({ ...form, hometown: e.target.value })}
                  placeholder="Charlotte, NC"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="profile-hometown-input"
                />
              </div>
              <div>
                <Label>Local Race Track</Label>
                <Input
                  value={form.local_track}
                  onChange={(e) => setForm({ ...form, local_track: e.target.value })}
                  placeholder="Hickory Motor Speedway"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="profile-track-input"
                />
              </div>
              <div>
                <Label>Biggest Win / Accomplishment</Label>
                <Input
                  value={form.biggest_win}
                  onChange={(e) => setForm({ ...form, biggest_win: e.target.value })}
                  placeholder="2025 Track Champion"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="profile-win-input"
                />
              </div>
              <div>
                <Label>Short Bio (250–500 characters recommended)</Label>
                <Textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 600) })}
                  placeholder="Tell other racers about your build, your favorite track, and what got you into Crown Vic racing…"
                  rows={5}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="profile-bio-input"
                />
                <p className="text-gray-500 text-xs mt-1 text-right">{form.bio.length}/600</p>
              </div>
            </div>

            <div className="mt-6 bg-[#1F1F1F] border border-[#262626] rounded p-4">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Public display preview</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#141414] border border-[#262626] overflow-hidden flex items-center justify-center">
                  {avatarUrl ? (
                    <img src={resolveImageUrl(avatarUrl)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{preview || '—'}</p>
                  {form.hometown && <p className="text-gray-500 text-xs">{form.hometown}</p>}
                </div>
              </div>
            </div>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="save-profile-btn">
              {saving ? 'Saving…' : firstVisit ? 'Save & View My Profile' : 'Save Changes'}
            </Button>
            {firstVisit && (
              <Button type="button" variant="outline" onClick={() => navigate('/')} className="border-[#262626] text-white" data-testid="skip-profile-btn">
                Skip for now
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
